import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Anchor = "top" | "bottom" | "left" | "right" | "middle";

interface Relation {
  targetId: string;
  targetAnchor: Anchor;
  sourceAnchor: Anchor;
}

interface ElementData {
  id: string;
  element: HTMLDivElement;
  relations: Relation[];
}

interface ConnectorContextValue {
  registerElement: (
    id: string,
    element: HTMLDivElement,
    relations: Relation[],
  ) => void;
  unregisterElement: (id: string) => void;
  strokeColor?: string;
  strokeWidth?: number;
}

const ConnectorContext = createContext<ConnectorContextValue | null>(null);

interface ConnectorContainerProps {
  children: React.ReactNode;
  strokeColor?: string;
  strokeWidth?: number;
  endMarker?: boolean;
  noCurves?: boolean;
  lineStyle?: "curve" | "straight";
  svgContainerStyle?: React.CSSProperties;
}

export function ConnectorContainer({
  children,
  strokeColor = "#888",
  strokeWidth = 2,
  lineStyle = "curve",
  svgContainerStyle,
}: ConnectorContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [elements, setElements] = useState<Map<string, ElementData>>(new Map());
  const [paths, setPaths] = useState<React.ReactElement[]>([]);

  // Stable, and so is the context value below. Registering sets state here,
  // which re-renders the container; a context value rebuilt on that render is a
  // new dependency for every element's registration effect, which registers
  // again — "Maximum update depth exceeded" on the Agents page.
  const registerElement = useCallback(
    (id: string, element: HTMLDivElement, relations: Relation[]) => {
      setElements((prev) => {
        const next = new Map(prev);
        next.set(id, { id, element, relations });
        return next;
      });
    },
    [],
  );

  const unregisterElement = useCallback((id: string) => {
    setElements((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const context = useMemo(
    () => ({ registerElement, unregisterElement, strokeColor, strokeWidth }),
    [registerElement, unregisterElement, strokeColor, strokeWidth],
  );

  const getAnchorPoint = (
    element: HTMLDivElement,
    anchor: Anchor,
    containerRect: DOMRect,
  ): { x: number; y: number } => {
    const rect = element.getBoundingClientRect();
    const x = rect.left - containerRect.left;
    const y = rect.top - containerRect.top;

    switch (anchor) {
      case "top":
        return { x: x + rect.width / 2, y: y };
      case "bottom":
        return { x: x + rect.width / 2, y: y + rect.height };
      case "left":
        return { x: x, y: y + rect.height / 2 };
      case "right":
        return { x: x + rect.width, y: y + rect.height / 2 };
      case "middle":
      default:
        return { x: x + rect.width / 2, y: y + rect.height / 2 };
    }
  };

  const calculatePaths = () => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newPaths: React.ReactElement[] = [];

    elements.forEach((sourceData) => {
      sourceData.relations.forEach((relation, index) => {
        const targetData = elements.get(relation.targetId);
        if (!targetData) return;

        const start = getAnchorPoint(
          sourceData.element,
          relation.sourceAnchor,
          containerRect,
        );
        const end = getAnchorPoint(
          targetData.element,
          relation.targetAnchor,
          containerRect,
        );

        const pathData =
          lineStyle === "curve"
            ? createCurvePath(start, end)
            : createStraightPath(start, end);

        newPaths.push(
          <path
            key={`${sourceData.id}-${relation.targetId}-${index}`}
            d={pathData}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeOpacity={0.4}
          />,
        );
      });
    });

    setPaths(newPaths);
  };

  const createCurvePath = (
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): string => {
    const midY = start.y + (end.y - start.y) / 2;
    return `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
  };

  const createStraightPath = (
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): string => {
    return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
  };

  useEffect(() => {
    calculatePaths();

    const handleResize = () => calculatePaths();
    const handleScroll = () => calculatePaths();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    const observer = new MutationObserver(() => calculatePaths());
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      observer.disconnect();
    };
  }, [elements, strokeColor, strokeWidth, lineStyle]);

  return (
    <ConnectorContext.Provider value={context}>
      <div ref={containerRef} style={{ position: "relative" }}>
        {children}
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
            ...svgContainerStyle,
          }}
        >
          {paths}
        </svg>
      </div>
    </ConnectorContext.Provider>
  );
}

interface ConnectorElementProps {
  id: string;
  children: React.ReactNode;
  relations?: Relation[];
}

const NO_RELATIONS: Relation[] = [];

export function ConnectorElement({
  id,
  children,
  relations = NO_RELATIONS,
}: ConnectorElementProps) {
  const context = useContext(ConnectorContext);
  const elementRef = useRef<HTMLDivElement>(null);

  // Callers build `relations` inline, so it is a new array on every render;
  // keyed on its content, the element only registers again when a line
  // actually changes.
  const relationsKey = JSON.stringify(relations);
  const latestRelations = useRef(relations);
  latestRelations.current = relations;

  useEffect(() => {
    if (elementRef.current && context) {
      context.registerElement(id, elementRef.current, latestRelations.current);
      return () => context.unregisterElement(id);
    }
  }, [id, relationsKey, context]);

  return (
    <div ref={elementRef} style={{ position: "relative" }}>
      {children}
    </div>
  );
}
